import { supabase } from '../lib/supabase';
import staffIdEmailService from './staffIdEmailService';

export const staffIdService = {
  // ============================================================================
  // TEMPLATE OPERATIONS
  // ============================================================================

  async getAllTemplates() {
    const { data, error } = await supabase
      ?.from('id_card_templates')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('is_default', { ascending: false })
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      templateName: row?.template_name,
      templateDescription: row?.template_description,
      companyLogoUrl: row?.company_logo_url,
      primaryColor: row?.primary_color,
      secondaryColor: row?.secondary_color,
      fontFamily: row?.font_family,
      layoutConfig: row?.layout_config,
      isActive: row?.is_active,
      isDefault: row?.is_default,
      createdBy: row?.created_by,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getTemplateById(id) {
    const { data, error } = await supabase
      ?.from('id_card_templates')
      ?.select('*')
      ?.eq('id', id)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      templateName: data?.template_name,
      templateDescription: data?.template_description,
      companyLogoUrl: data?.company_logo_url,
      primaryColor: data?.primary_color,
      secondaryColor: data?.secondary_color,
      fontFamily: data?.font_family,
      layoutConfig: data?.layout_config,
      isActive: data?.is_active,
      isDefault: data?.is_default,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async createTemplate(template) {
    const { data, error } = await supabase
      ?.from('id_card_templates')
      ?.insert({
        template_name: template?.templateName,
        template_description: template?.templateDescription,
        company_logo_url: template?.companyLogoUrl,
        primary_color: template?.primaryColor,
        secondary_color: template?.secondaryColor,
        font_family: template?.fontFamily,
        layout_config: template?.layoutConfig,
        is_active: template?.isActive ?? true,
        is_default: template?.isDefault ?? false,
        created_by: template?.createdBy
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      templateName: data?.template_name,
      createdAt: data?.created_at
    };
  },

  async updateTemplate(id, template) {
    const { data, error } = await supabase
      ?.from('id_card_templates')
      ?.update({
        template_name: template?.templateName,
        template_description: template?.templateDescription,
        company_logo_url: template?.companyLogoUrl,
        primary_color: template?.primaryColor,
        secondary_color: template?.secondaryColor,
        font_family: template?.fontFamily,
        layout_config: template?.layoutConfig,
        is_active: template?.isActive,
        is_default: template?.isDefault,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      templateName: data?.template_name,
      updatedAt: data?.updated_at
    };
  },

  // ============================================================================
  // ID CARD OPERATIONS
  // ============================================================================

  async getAllCards() {
    const { data, error } = await supabase
      ?.from('id_cards')
      ?.select(`
        *,
        employee:employees(*),
        template:id_card_templates(*)
      `)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      templateId: row?.template_id,
      cardNumber: row?.card_number,
      issueDate: row?.issue_date,
      expiryDate: row?.expiry_date,
      status: row?.status,
      barcodeData: row?.barcode_data,
      qrCodeData: row?.qr_code_data,
      cardImageUrl: row?.card_image_url,
      printCount: row?.print_count,
      lastPrintedAt: row?.last_printed_at,
      notes: row?.notes,
      createdBy: row?.created_by,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
      employee: row?.employee ? {
        id: row?.employee?.id,
        employeeNumber: row?.employee?.employee_number,
        staffNumber: row?.employee?.staff_number,
        firstName: row?.employee?.first_name,
        lastName: row?.employee?.last_name,
        department: row?.employee?.department,
        position: row?.employee?.position,
        photoUrl: row?.employee?.photo_url
      } : null,
      template: row?.template ? {
        id: row?.template?.id,
        templateName: row?.template?.template_name,
        primaryColor: row?.template?.primary_color,
        secondaryColor: row?.template?.secondary_color
      } : null
    })) || [];
  },

  async getCardById(id) {
    const { data, error } = await supabase
      ?.from('id_cards')
      ?.select(`
        *,
        employee:employees(*),
        template:id_card_templates(*)
      `)
      ?.eq('id', id)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      employeeId: data?.employee_id,
      templateId: data?.template_id,
      cardNumber: data?.card_number,
      issueDate: data?.issue_date,
      expiryDate: data?.expiry_date,
      status: data?.status,
      barcodeData: data?.barcode_data,
      qrCodeData: data?.qr_code_data,
      cardImageUrl: data?.card_image_url,
      printCount: data?.print_count,
      lastPrintedAt: data?.last_printed_at,
      notes: data?.notes,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      employee: data?.employee ? {
        id: data?.employee?.id,
        employeeNumber: data?.employee?.employee_number,
        staffNumber: data?.employee?.staff_number,
        firstName: data?.employee?.first_name,
        lastName: data?.employee?.last_name,
        department: data?.employee?.department,
        position: data?.employee?.position,
        photoUrl: data?.employee?.photo_url,
        email: data?.employee?.email
      } : null,
      template: data?.template
    };
  },

  async createCard(card) {
    const { data, error } = await supabase
      ?.from('id_cards')
      ?.insert({
        employee_id: card?.employeeId,
        template_id: card?.templateId,
        card_number: card?.cardNumber,
        issue_date: card?.issueDate,
        expiry_date: card?.expiryDate,
        status: card?.status || 'draft',
        barcode_data: card?.barcodeData,
        qr_code_data: card?.qrCodeData,
        card_image_url: card?.cardImageUrl,
        notes: card?.notes,
        created_by: card?.createdBy
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      cardNumber: data?.card_number,
      createdAt: data?.created_at
    };
  },

  async updateCard(id, card) {
    const { data, error } = await supabase
      ?.from('id_cards')
      ?.update({
        template_id: card?.templateId,
        issue_date: card?.issueDate,
        expiry_date: card?.expiryDate,
        status: card?.status,
        barcode_data: card?.barcodeData,
        qr_code_data: card?.qrCodeData,
        card_image_url: card?.cardImageUrl,
        print_count: card?.printCount,
        last_printed_at: card?.lastPrintedAt,
        notes: card?.notes,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      cardNumber: data?.card_number,
      updatedAt: data?.updated_at
    };
  },

  async deleteCard(id) {
    const { error } = await supabase
      ?.from('id_cards')
      ?.delete()
      ?.eq('id', id);

    if (error) throw error;
  },

  async batchCreateCards(cards) {
    const { data, error } = await supabase
      ?.from('id_cards')
      ?.insert(cards?.map(card => ({
        employee_id: card?.employeeId,
        template_id: card?.templateId,
        card_number: card?.cardNumber,
        issue_date: card?.issueDate,
        expiry_date: card?.expiryDate,
        status: card?.status || 'draft',
        barcode_data: card?.barcodeData,
        qr_code_data: card?.qrCodeData,
        created_by: card?.createdBy
      })))
      ?.select();

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      cardNumber: row?.card_number,
      employeeId: row?.employee_id
    })) || [];
  },

  // ============================================================================
  // GENERATION LOG OPERATIONS
  // ============================================================================

  async createGenerationLog(log) {
    const { data, error } = await supabase
      ?.from('id_card_generation_logs')
      ?.insert({
        card_id: log?.cardId,
        action_type: log?.actionType,
        performed_by: log?.performedBy,
        printer_model: log?.printerModel,
        export_format: log?.exportFormat,
        batch_id: log?.batchId,
        success: log?.success ?? true,
        error_message: log?.errorMessage,
        metadata: log?.metadata
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      performedAt: data?.performed_at
    };
  },

  async getGenerationLogs(cardId) {
    const { data, error } = await supabase
      ?.from('id_card_generation_logs')
      ?.select('*')
      ?.eq('card_id', cardId)
      ?.order('performed_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      cardId: row?.card_id,
      actionType: row?.action_type,
      performedBy: row?.performed_by,
      printerModel: row?.printer_model,
      exportFormat: row?.export_format,
      batchId: row?.batch_id,
      success: row?.success,
      errorMessage: row?.error_message,
      metadata: row?.metadata,
      performedAt: row?.performed_at
    })) || [];
  },

  async getBatchLogs(batchId) {
    const { data, error } = await supabase
      ?.from('id_card_generation_logs')
      ?.select('*')
      ?.eq('batch_id', batchId)
      ?.order('performed_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      cardId: row?.card_id,
      actionType: row?.action_type,
      performedBy: row?.performed_by,
      success: row?.success,
      errorMessage: row?.error_message,
      performedAt: row?.performed_at
    })) || [];
  },

  // ============================================================================
  // STAFF ID APPROVAL WORKFLOW METHODS
  // ============================================================================

  async submitStaffIdForApproval(cardId, employeeId, templateId, submittedBy) {
    const { data, error } = await supabase
      ?.rpc('initiate_staff_id_approval', {
        p_card_id: cardId,
        p_employee_id: employeeId,
        p_template_id: templateId,
        p_initiated_by: submittedBy
      });
    
    if (error) throw error;
    
    // Process pending email notifications
    await this.processPendingNotifications();
    
    return data;
  },

  async getStaffIdApprovalStatus(cardId) {
    const { data, error } = await supabase
      ?.from('id_cards')
      ?.select(`
        id,
        card_number,
        approval_status,
        approval_instance_id,
        submitted_for_approval_at,
        submitted_by,
        approved_at,
        rejected_at,
        rejection_reason,
        employee:employees(
          id,
          employee_number,
          first_name,
          last_name,
          department,
          position
        ),
        approval_instance:approval_instances(
          id,
          current_step_order,
          overall_status,
          initiated_at,
          completed_at
        )
      `)
      ?.eq('id', cardId)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      cardNumber: data?.card_number,
      approvalStatus: data?.approval_status,
      approvalInstanceId: data?.approval_instance_id,
      submittedForApprovalAt: data?.submitted_for_approval_at,
      submittedBy: data?.submitted_by,
      approvedAt: data?.approved_at,
      rejectedAt: data?.rejected_at,
      rejectionReason: data?.rejection_reason,
      employee: data?.employee ? {
        id: data?.employee?.id,
        employeeNumber: data?.employee?.employee_number,
        firstName: data?.employee?.first_name,
        lastName: data?.employee?.last_name,
        department: data?.employee?.department,
        position: data?.employee?.position
      } : null,
      approvalInstance: data?.approval_instance ? {
        id: data?.approval_instance?.id,
        currentStepOrder: data?.approval_instance?.current_step_order,
        overallStatus: data?.approval_instance?.overall_status,
        initiatedAt: data?.approval_instance?.initiated_at,
        completedAt: data?.approval_instance?.completed_at
      } : null
    };
  },

  async getPendingStaffIdApprovals(userRole) {
    const { data, error } = await supabase
      ?.from('approval_step_executions')
      ?.select(`
        id,
        step_order,
        step_name,
        assigned_to_role,
        assigned_to_email,
        step_status,
        timeout_at,
        created_at,
        approval_instance:approval_instances(
          id,
          entity_type,
          entity_id,
          entity_metadata,
          initiated_by,
          initiated_at
        )
      `)
      ?.eq('step_status', 'pending')
      ?.eq('assigned_to_role', userRole)
      ?.order('created_at', { ascending: true });
    
    if (error) throw error;
    
    // Filter for staff_id_generation entity type
    const staffIdApprovals = data?.filter(
      row => row?.approval_instance?.entity_type === 'staff_id_generation'
    ) || [];
    
    return staffIdApprovals?.map(row => ({
      id: row?.id,
      stepOrder: row?.step_order,
      stepName: row?.step_name,
      assignedToRole: row?.assigned_to_role,
      assignedToEmail: row?.assigned_to_email,
      stepStatus: row?.step_status,
      timeoutAt: row?.timeout_at,
      createdAt: row?.created_at,
      approvalInstanceId: row?.approval_instance?.id,
      entityType: row?.approval_instance?.entity_type,
      entityId: row?.approval_instance?.entity_id,
      entityMetadata: row?.approval_instance?.entity_metadata,
      initiatedBy: row?.approval_instance?.initiated_by,
      initiatedAt: row?.approval_instance?.initiated_at
    })) || [];
  },

  async approveStaffIdStep(stepExecutionId, approvedBy, comments) {
    const { data, error } = await supabase
      ?.rpc('handle_staff_id_approval_step', {
        p_step_execution_id: stepExecutionId,
        p_approved_by: approvedBy,
        p_action: 'approve',
        p_comments: comments
      });
    
    if (error) throw error;
    
    // Process pending email notifications
    await this.processPendingNotifications();
    
    return data;
  },

  async rejectStaffIdStep(stepExecutionId, rejectedBy, rejectionReason, comments) {
    const { data, error } = await supabase
      ?.rpc('handle_staff_id_approval_step', {
        p_step_execution_id: stepExecutionId,
        p_approved_by: rejectedBy,
        p_action: 'reject',
        p_comments: comments,
        p_rejection_reason: rejectionReason
      });
    
    if (error) throw error;
    
    // Process pending email notifications
    await this.processPendingNotifications();
    
    return data;
  },

  async resubmitStaffIdForApproval(cardId, resubmittedBy, resubmissionNotes) {
    const { data, error } = await supabase
      ?.rpc('resubmit_staff_id_approval', {
        p_card_id: cardId,
        p_resubmitted_by: resubmittedBy,
        p_resubmission_notes: resubmissionNotes
      });
    
    if (error) throw error;
    
    return data;
  },

  async getStaffIdApprovalHistory(approvalInstanceId) {
    const { data, error } = await supabase
      ?.from('approval_history')
      ?.select('*')
      ?.eq('approval_instance_id', approvalInstanceId)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      approvalInstanceId: row?.approval_instance_id,
      actionType: row?.action_type,
      actionDescription: row?.action_description,
      performedBy: row?.performed_by,
      stepOrder: row?.step_order,
      previousStatus: row?.previous_status,
      newStatus: row?.new_status,
      metadata: row?.metadata,
      createdAt: row?.created_at
    })) || [];
  },

  async getStaffIdApprovalSteps(approvalInstanceId) {
    const { data, error } = await supabase
      ?.from('approval_step_executions')
      ?.select('*')
      ?.eq('approval_instance_id', approvalInstanceId)
      ?.order('step_order', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      approvalInstanceId: row?.approval_instance_id,
      stepId: row?.step_id,
      stepOrder: row?.step_order,
      stepName: row?.step_name,
      assignedToRole: row?.assigned_to_role,
      assignedToEmail: row?.assigned_to_email,
      stepStatus: row?.step_status,
      approvedBy: row?.approved_by,
      approvedAt: row?.approved_at,
      rejectionReason: row?.rejection_reason,
      comments: row?.comments,
      timeoutAt: row?.timeout_at,
      createdAt: row?.created_at
    })) || [];
  },

  async getAllStaffIdApprovalsWithStatus() {
    const { data, error } = await supabase
      ?.from('id_cards')
      ?.select(`
        id,
        card_number,
        approval_status,
        submitted_for_approval_at,
        submitted_by,
        approved_at,
        rejected_at,
        rejection_reason,
        employee:employees(
          id,
          employee_number,
          first_name,
          last_name,
          department,
          position
        ),
        approval_instance:approval_instances(
          id,
          current_step_order,
          overall_status
        )
      `)
      ?.not('approval_status', 'is', null)
      ?.order('submitted_for_approval_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      cardNumber: row?.card_number,
      approvalStatus: row?.approval_status,
      submittedForApprovalAt: row?.submitted_for_approval_at,
      submittedBy: row?.submitted_by,
      approvedAt: row?.approved_at,
      rejectedAt: row?.rejected_at,
      rejectionReason: row?.rejection_reason,
      employee: row?.employee ? {
        id: row?.employee?.id,
        employeeNumber: row?.employee?.employee_number,
        firstName: row?.employee?.first_name,
        lastName: row?.employee?.last_name,
        fullName: `${row?.employee?.first_name} ${row?.employee?.last_name}`,
        department: row?.employee?.department,
        position: row?.employee?.position
      } : null,
      approvalInstance: row?.approval_instance ? {
        id: row?.approval_instance?.id,
        currentStepOrder: row?.approval_instance?.current_step_order,
        overallStatus: row?.approval_instance?.overall_status
      } : null
    })) || [];
  },

  // ============================================================================
  // EMAIL NOTIFICATION PROCESSING
  // ============================================================================

  async processPendingNotifications() {
    try {
      // Get pending notifications from queue
      const { data: notifications, error } = await supabase
        ?.from('notification_queue')
        ?.select('*')
        ?.eq('status', 'pending')
        ?.lt('attempts', 3)
        ?.order('created_at', { ascending: true })
        ?.limit(10);

      if (error) {
        console.error('Error fetching pending notifications:', error);
        return;
      }

      if (!notifications || notifications?.length === 0) {
        return;
      }

      // Process each notification
      for (const notification of notifications) {
        try {
          await this.sendNotificationEmail(notification);
          
          // Mark as processed
          await supabase
            ?.from('notification_queue')
            ?.update({
              status: 'sent',
              processed_at: new Date()?.toISOString()
            })
            ?.eq('id', notification?.id);
        } catch (emailError) {
          console.error('Error sending notification email:', emailError);
          
          // Update attempt count and error message
          await supabase
            ?.from('notification_queue')
            ?.update({
              attempts: (notification?.attempts || 0) + 1,
              last_attempt_at: new Date()?.toISOString(),
              error_message: emailError?.message,
              status: (notification?.attempts || 0) >= 2 ? 'failed' : 'pending'
            })
            ?.eq('id', notification?.id);
        }
      }
    } catch (error) {
      console.error('Error processing pending notifications:', error);
    }
  },

  async sendNotificationEmail(notification) {
    const { notification_type, recipient_emails, metadata } = notification;
    const cardData = metadata?.card_data || {};

    // Filter out null/undefined emails
    const validEmails = recipient_emails?.filter(email => email && email?.includes('@')) || [];

    if (validEmails?.length === 0) {
      throw new Error('No valid recipient emails found');
    }

    // Send appropriate email based on notification type
    switch (notification_type) {
      case 'staff_id_submission':
        await staffIdEmailService?.sendStaffIdSubmissionEmail(
          {
            cardId: cardData?.card_id,
            cardNumber: cardData?.card_number,
            employeeName: cardData?.employee_name,
            employeeNumber: cardData?.employee_number,
            department: cardData?.department,
            position: cardData?.position,
            submittedBy: cardData?.submitted_by,
            submittedAt: cardData?.submitted_at,
            approvalLevel: metadata?.approval_level
          },
          validEmails
        );
        break;

      case 'staff_id_step_approval': case'staff_id_final_approval':
        await staffIdEmailService?.sendStaffIdApprovalEmail(
          {
            cardId: cardData?.card_id,
            cardNumber: cardData?.card_number,
            employeeName: cardData?.employee_name,
            employeeNumber: cardData?.employee_number,
            department: cardData?.department,
            position: cardData?.position,
            approvedAt: cardData?.approved_at,
            approvalLevel: cardData?.approval_level,
            comments: cardData?.comments,
            isFinalApproval: metadata?.is_final_approval
          },
          validEmails,
          cardData?.approved_by
        );
        break;

      case 'staff_id_rejection':
        await staffIdEmailService?.sendStaffIdRejectionEmail(
          {
            cardId: cardData?.card_id,
            cardNumber: cardData?.card_number,
            employeeName: cardData?.employee_name,
            employeeNumber: cardData?.employee_number,
            department: cardData?.department,
            position: cardData?.position,
            rejectedAt: cardData?.rejected_at,
            rejectionReason: cardData?.rejection_reason,
            comments: cardData?.comments,
            approvalLevel: cardData?.approval_level
          },
          validEmails,
          cardData?.approved_by
        );
        break;

      default:
        throw new Error(`Unknown notification type: ${notification_type}`);
    }
  }
};

export default staffIdService;